import { useMemo, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useCoupangReviewFailures, useVerifyCoupangReviewFailure } from '@/apps/apis/coupangReviewFailures/hooks';
import type {
  CoupangReviewComposition,
  CoupangReviewFailure,
  CoupangReviewState,
} from '@/apps/apis/coupangReviewFailures/types';
import { usePublishedProducts } from '@/apps/hooks/product/useProductsBySkus';
import type { SkinSeoulAdminProduct } from '@/models/ProductTypes';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 25;
const isSafeCoupangProductUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      url.hostname === 'www.coupang.com' &&
      url.port === '' &&
      url.username === '' &&
      url.password === '' &&
      url.hash === '' &&
      /^\/vp\/products\/[1-9][0-9]*$/.test(url.pathname)
    );
  } catch {
    return false;
  }
};

const isSafeSkinSeoulProductUrl = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      ['skin-seoul.com', 'dev.skin-seoul.com'].includes(url.hostname) &&
      url.port === '' &&
      url.username === '' &&
      url.password === '' &&
      url.hash === '' &&
      /^\/product\/[^/]+\/$/.test(url.pathname)
    );
  } catch {
    return false;
  }
};

const safeImageUrl = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.username === '' && url.password === '' ? value : undefined;
  } catch {
    return undefined;
  }
};

const REASON_LABELS: Record<string, string> = {
  'composition-mismatch': 'Coupang option does not match the SKU composition',
  'composition-unknown': 'Coupang option composition could not be verified',
};

const EXPECTED_FIELDS = [
  ['itemName', 'Item name'],
  ['pack', 'Pack'],
  ['size', 'Size'],
  ['color', 'Color'],
] as const;

// Facade compares only the item name when it exists, so every mismatch belongs to that row. Without it,
// attributes are compared and a color difference is reported as 'option'.
const isMismatchedRow = (
  composition: CoupangReviewComposition,
  field: (typeof EXPECTED_FIELDS)[number][0],
): boolean => {
  if (composition.expected.itemName) {
    return field === 'itemName' && composition.mismatchFields.length > 0;
  }
  return field !== 'itemName' && composition.mismatchFields.includes(field === 'color' ? 'option' : field);
};

const formatDate = (value?: string): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const readableError = (error: unknown): string => {
  const response = error as { response?: { status?: number; data?: { message?: string } } };
  if (response.response?.status === 409) {
    return 'This review changed. The latest value has been reloaded.';
  }
  if (response.response?.data?.message) {
    return response.response.data.message;
  }
  return error instanceof Error ? error.message : 'Unable to verify this Coupang URL.';
};

const CoupangFailuresPage = () => <CoupangFailuresList />;

const CoupangFailuresList = () => {
  const [state, setState] = useState<CoupangReviewState>('PENDING_REVIEW');
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [draftUrlByProductId, setDraftUrlByProductId] = useState<Record<number, string>>({});
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const cursor = cursors[pageIndex];
  const reviews = useCoupangReviewFailures({ state, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) });
  const verify = useVerifyCoupangReviewFailure();
  const reviewSkus = useMemo(
    () => [...new Set((reviews.data?.items ?? []).map((failure) => failure.sku))],
    [reviews.data?.items],
  );
  const linkedProducts = usePublishedProducts(reviewSkus);
  const productByBinding = useMemo(() => {
    const products = new Map<string, SkinSeoulAdminProduct | undefined>();
    for (const product of linkedProducts.products) {
      const productId = Number(product.productId);
      if (!product.sku || !Number.isSafeInteger(productId) || productId < 1) {
        continue;
      }
      const binding = `${product.sku}:${productId}`;
      products.set(binding, products.has(binding) ? undefined : product);
    }
    return products;
  }, [linkedProducts.products]);

  const changeState = (nextState: CoupangReviewState) => {
    setState(nextState);
    setCursors([undefined]);
    setPageIndex(0);
    setVerifyError(null);
  };

  const nextPage = () => {
    if (!reviews.data?.nextCursor) {
      return;
    }
    setCursors((previous) => [...previous.slice(0, pageIndex + 1), reviews.data?.nextCursor]);
    setPageIndex((previous) => previous + 1);
  };

  const verifyFailure = (failure: CoupangReviewFailure) => {
    const proposedCpUrl = draftUrlByProductId[failure.productId] ?? failure.currentCpUrl;
    setVerifyError(null);
    verify.mutate(
      {
        productId: failure.productId,
        request: {
          expectedSku: failure.sku,
          currentCpUrl: failure.currentCpUrl,
          expectedCurrentStateHash: failure.expectedCurrentStateHash,
          expectedReviewVersion: failure.version,
          proposedCpUrl,
        },
      },
      {
        onSuccess: () => {
          setDraftUrlByProductId((previous) => {
            const next = { ...previous };
            delete next[failure.productId];
            return next;
          });
        },
        onError: (error) => {
          setVerifyError(readableError(error));
          if ((error as { response?: { status?: number } }).response?.status === 409) {
            void reviews.refetch();
          }
        },
      },
    );
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-end gap-3 flex-wrap mb-3">
        <div>
          <h2 className="mb-1">Coupang failures</h2>
          <p className="text-muted mb-0">Verify a URL to make this product eligible only for a future batch.</p>
        </div>
        <Form.Group style={{ minWidth: 220 }}>
          <Form.Label className="small text-muted">Review state</Form.Label>
          <Form.Select value={state} onChange={(event) => changeState(event.target.value as CoupangReviewState)}>
            <option value="PENDING_REVIEW">Pending review</option>
            <option value="READY_FOR_NEXT_BATCH">Ready for next batch</option>
            <option value="RESOLVED">Resolved</option>
          </Form.Select>
        </Form.Group>
      </div>

      {verifyError ? <Alert variant="danger">{verifyError}</Alert> : null}
      {reviews.isLoading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : null}
      {reviews.error ? (
        <Alert variant="danger">Failed to load Coupang failures: {(reviews.error as Error).message}</Alert>
      ) : null}
      {!reviews.isLoading && !reviews.error ? (
        <Card>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>SkinSeoul product</th>
                    <th>Reason</th>
                    <th>Failed</th>
                    <th>State</th>
                    <th>Failed URL</th>
                    <th>Current URL</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.data?.items.map((failure) => (
                    <CoupangFailureRow
                      key={failure.productId}
                      failure={failure}
                      linkedProduct={productByBinding.get(`${failure.sku}:${failure.productId}`)}
                      isProductLoading={linkedProducts.loading}
                      draftUrl={draftUrlByProductId[failure.productId] ?? failure.currentCpUrl}
                      isVerifying={verify.isPending}
                      onDraftUrlChange={(value) =>
                        setDraftUrlByProductId((previous) => ({ ...previous, [failure.productId]: value }))
                      }
                      onVerify={() => verifyFailure(failure)}
                    />
                  ))}
                  {reviews.data?.items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        No Coupang failures match this state.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      ) : null}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          variant="outline-secondary"
          onClick={() => setPageIndex((previous) => previous - 1)}
          disabled={pageIndex === 0}
        >
          Previous
        </Button>
        <Button variant="outline-primary" onClick={nextPage} disabled={!reviews.data?.nextCursor}>
          Next
        </Button>
      </div>
    </Container>
  );
};

interface CoupangFailureRowProps {
  failure: CoupangReviewFailure;
  linkedProduct: SkinSeoulAdminProduct | undefined;
  isProductLoading: boolean;
  draftUrl: string;
  isVerifying: boolean;
  onDraftUrlChange: (value: string) => void;
  onVerify: () => void;
}

const CoupangFailureRow = ({
  failure,
  linkedProduct,
  isProductLoading,
  draftUrl,
  isVerifying,
  onDraftUrlChange,
  onVerify,
}: CoupangFailureRowProps) => {
  const failedUrl =
    failure.failedCpUrl && isSafeCoupangProductUrl(failure.failedCpUrl) ? failure.failedCpUrl : undefined;
  const currentUrl = isSafeCoupangProductUrl(failure.currentCpUrl) ? failure.currentCpUrl : undefined;
  return (
    <tr>
      <td style={{ minWidth: 300 }}>
        <SkinSeoulProductContext failure={failure} product={linkedProduct} isLoading={isProductLoading} />
      </td>
      <td style={{ minWidth: 260 }}>
        <div>{failure.reason ? (REASON_LABELS[failure.reason] ?? failure.reason) : '—'}</div>
        <CompositionComparison composition={failure.composition} />
      </td>
      <td>{formatDate(failure.failedAt)}</td>
      <td>{failure.state}</td>
      <td className="text-break">
        {failedUrl ? (
          <a href={failedUrl} target="_blank" rel="noopener noreferrer">
            {failedUrl}
          </a>
        ) : failure.failedCpUrl ? (
          <span className="text-muted">Unsafe URL not opened</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="text-break">
        {currentUrl ? (
          <a href={currentUrl} target="_blank" rel="noopener noreferrer">
            {currentUrl}
          </a>
        ) : (
          <span className="text-muted">Unsafe URL not opened</span>
        )}
      </td>
      <td style={{ minWidth: 350 }}>
        <Form.Control
          aria-label={`Coupang URL for ${failure.sku}`}
          value={draftUrl}
          onChange={(event) => onDraftUrlChange(event.target.value)}
          disabled={isVerifying}
          className="mb-2"
        />
        <Button variant="primary" size="sm" onClick={onVerify} disabled={isVerifying}>
          {isVerifying ? 'Verifying…' : 'Verify and retry next batch'}
        </Button>
        {failure.state !== 'RESOLVED' ? (
          <div className="small text-muted mt-2">
            CP cost on hold. Submitting only queues a re-scrape (Ready for next batch); it does not approve the price.
          </div>
        ) : null}
      </td>
    </tr>
  );
};

const CompositionComparison = ({ composition }: { composition: CoupangReviewComposition | undefined }) => {
  if (!composition) {
    return <div className="small text-muted">No composition evidence</div>;
  }
  // Facade judges from attributeTitle, falling back to productName; the Coupang itemName is never compared.
  const actualOption = composition.actual.attributeTitle || composition.actual.productName;
  return (
    <Table size="sm" bordered className="small mb-0 mt-1">
      <tbody>
        <tr>
          <th colSpan={2}>Composition: {composition.verdict}</th>
        </tr>
        {EXPECTED_FIELDS.map(([field, label]) => (
          <tr key={field} className={isMismatchedRow(composition, field) ? 'table-danger' : undefined}>
            <th scope="row">Expected {label}</th>
            <td>{composition.expected[field] || <span className="text-muted">unknown</span>}</td>
          </tr>
        ))}
        <tr>
          <th scope="row">Coupang option</th>
          <td>{actualOption || <span className="text-muted">unknown</span>}</td>
        </tr>
        {composition.mismatchFields.length > 0 ? (
          <tr className="table-danger">
            <th scope="row">Mismatched</th>
            <td>{composition.mismatchFields.join(', ')}</td>
          </tr>
        ) : null}
      </tbody>
    </Table>
  );
};

interface SkinSeoulProductContextProps {
  failure: CoupangReviewFailure;
  product: SkinSeoulAdminProduct | undefined;
  isLoading: boolean;
}

const SkinSeoulProductContext = ({ failure, product, isLoading }: SkinSeoulProductContextProps) => {
  const imageUrl = safeImageUrl(product?.productImageUrls?.[0]);
  const storefrontUrl = isSafeSkinSeoulProductUrl(product?.productLink) ? product?.productLink : undefined;
  return (
    <div className="d-flex gap-2 align-items-start">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={product?.name ?? failure.sku}
          width={64}
          height={64}
          loading="lazy"
          className="border rounded flex-shrink-0"
          style={{ objectFit: 'cover' }}
        />
      ) : (
        <div
          aria-label="No product image"
          className="border rounded bg-light text-muted d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 64, height: 64, fontSize: 11 }}
        >
          No image
        </div>
      )}
      <div>
        {product ? (
          <>
            <div className="fw-semibold">{product.name || 'Unnamed product'}</div>
            {product.nameKor ? <div>{product.nameKor}</div> : null}
            <div className="small text-muted">{product.brand || 'Unknown brand'}</div>
          </>
        ) : (
          <div className="fw-semibold text-muted">
            {isLoading ? 'Loading product details…' : 'Product details unavailable'}
          </div>
        )}
        <div className="small text-muted">SKU {failure.sku}</div>
        <div className="small text-muted">Product ID {failure.productId}</div>
        <div className="d-flex flex-wrap gap-2 mt-1 small">
          {storefrontUrl ? (
            <a href={storefrontUrl} target="_blank" rel="noopener noreferrer">
              Open storefront
            </a>
          ) : null}
          <Link to={ROUTES.catalog.ssEditorWithSku(failure.sku)}>Open in SS Editor</Link>
        </div>
      </div>
    </div>
  );
};

export default CoupangFailuresPage;
